/**
 * 조회 카드 카탈로그 — 에이전트가 **그림으로** 보여 주는 것들 (DOC-23 §11).
 *
 * `actions.ts` 와 목록을 나눈 이유는 성격이 다르기 때문이다.
 *
 *   actions  쓰기 **초안**. 카드가 뜨고 **사람이 눌러야** 실행된다(§5.4)
 *   views    조회 **결과**. 누를 것이 없다. 답변 문장과 같은 값을 그림으로 옮긴 것뿐이다
 *
 * 한 목록에 섞으면 "이 카드는 눌러야 하나" 가 카드마다 달라진다 — 초안 카드의 규칙(확인은 사람이
 * 한다)이 흐려지는 것이 그 대가다.
 *
 * ## 모르는 kind 를 만나면
 *
 * `action` 과 달리 **조용히 넘긴다.** 초안이 사라지면 사용자의 요청이 증발하지만, 조회 카드는
 * 같은 내용이 이미 답변 문장에 있다 — 그림이 하나 없을 뿐이다. 그 대신 어긋남은 배포 전에 잡는다:
 * `agent/tests/test_views_sync.py` 가 에이전트가 내는 kind 와 이 목록을 대조한다.
 */

export const VIEWS = {
  /** 월간 회의 일정. 물어본 기간만큼의 주(週)만 그린다 — 빈 달을 붙이지 않는다. */
  meeting_calendar: { title: '회의 일정' },
  /**
   * 되물음 선택지. 후보가 여럿이라 도구가 **답하지 않고 물은** 자리다(DOC-20 §1.9).
   *
   * 다른 카드와 달리 **누르는 것**이지만 `actions` 가 아니다 — 눌러도 아무것도 실행되지 않고,
   * 그 문장이 새 사용자 발화로 나갈 뿐이다(빈 화면 제안 칩과 같은 동작). 확인 게이트가 필요한
   * 것은 쓰기이고, 이건 되묻기다.
   *
   * 같은 내용이 답변 문장에도 들어 있다. 칩은 사용자가 후보 이름을 다시 타이핑하지 않게 할 뿐이라,
   * 이 카드를 못 그려도 대화는 성립한다.
   */
  choice: { title: '어느 것을 볼까요' },
  /**
   * 본사 평면도에서 짚은 <b>한 사람의 자리</b>. "○○ 어디 앉아?" 의 답이다.
   *
   * <p>답변 문장은 "3층에 있습니다" 한 줄이고, 카드가 <b>그 자리</b>를 보여 준다 — 층만 듣고
   * 찾아가는 것과 도면에서 자리를 보고 가는 것은 다른 일이다. 회의 일정이 문장과 달력으로 함께
   * 나가는 것과 같은 결이다.
   */
  floorplan: { title: '자리 위치' },
  /**
   * 현장 하나의 <b>주단위 공정율 추이</b> — 예정·실적 두 선 (2026-08-14 · DOC-33).
   *
   * <p>문장은 최근 몇 주만 말하고 <b>그림이 전 구간</b>을 갖는다. 자리 물음에 "3층입니다" 한 줄과
   * 도면 카드를 함께 내는 것과 같은 결이다 — 접은 값과 접기 전 값을 나란히 둔다.
   *
   * <p>카드가 <b>반드시 말해야 하는 것</b> 둘: 예정 곡선이 계획 공정표 파일에서 온 것인지
   * 착수·완료일로 만든 것인지(`fromFile`), 그리고 계획 일정이 없는 공정이 있어 예정 곡선이
   * 100% 에 닿지 않는지(`datedCount &lt; leafCount`). 지어낸 곡선을 계획으로 오해하면
   * 그 차이가 발주처 보고까지 간다.
   */
  progress_curve: { title: '공정율 추이' },
} as const;

export type ViewKind = keyof typeof VIEWS;

export function isViewKind(kind: string): kind is ViewKind {
  return kind in VIEWS;
}
